import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, Theme, SxProps, Tooltip } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const STRINGS = appStrings.modules.start.whatIsACareerTrackModal;

// Tooltip data for each professional tier
const PROFESSIONAL_TIER_DATA: { id: string; salary: string }[] = [
  {
    id: 'semi-skilled',
    salary: STRINGS.tierSalaryData['semi-skilled'],
  },
  {
    id: 'trades-technical',
    salary: STRINGS.tierSalaryData['trades-technical'],
  },
  {
    id: 'middle-tier-pro',
    salary: STRINGS.tierSalaryData['middle-tier-pro'],
  },
  {
    id: 'upper-tier-pro',
    salary: STRINGS.tierSalaryData['upper-tier-pro'],
  },
  {
    id: 'top-tier-pro',
    salary: STRINGS.tierSalaryData['top-tier-pro'],
  },
];

function Header({ onClose }: { onClose?: () => void }) {
  return (
    <Box sx={headerContainerSx}>
      <Box sx={headerTextSx}>
        <Typography
          variant="display-md-semibold"
          color={(theme: Theme) => theme.backrs.colors['Colors/Base/White']}
        >
          {STRINGS.header.title}
        </Typography>
        <Typography
          variant="text-lg-medium"
          color={(theme: Theme) => theme.backrs.colors['Colors/Base/White']}
          sx={headerSubtitleSx}
        >
          {STRINGS.header.subtitle}
        </Typography>
      </Box>
      {onClose && (
        <Box id="career-track-modal-close-button" sx={closeIconContainerSx} onClick={onClose}>
          <AppIcon diameter="24px" color="Colors/Base/White" iconName="XClose" />
        </Box>
      )}
    </Box>
  );
}

function TopLeftSection() {
  return (
    <Box sx={topLeftSectionContainerSx}>
      <Box sx={topLeftSectionHeaderSx}>
        <Typography variant="display-sm-bold">{STRINGS.topLeftSection.title}</Typography>
        <Box sx={topLeftSectionImageSx} component="img" src="/images/image-start.svg" />
      </Box>

      <Box sx={descriptionContainerSx}>
        <Typography variant="text-sm-regular">
          <ReactMarkdown>{STRINGS.topLeftSection.description}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
}

function TopRightSection() {
  return (
    <Box sx={topRightSectionContainerSx}>
      <Box component="img" src="/images/static-career-tracks.svg" sx={topRightSectionImageSx} />
    </Box>
  );
}

// NOTE: Commenting this out for now, will add back when we have the correct graphic designed for the BottomRightSection

// function BottomLeftSection() {
//   return (
//     <Box sx={bottomLeftSectionContainerSx}>
//       <Box sx={sectionHeaderSx}>
//         <Typography variant="display-sm-bold">{STRINGS.bottomLeftSection.title}</Typography>
//       </Box>
//       <Box>
//         <Typography variant="text-sm-regular">
//           <ReactMarkdown>{STRINGS.bottomLeftSection.description}</ReactMarkdown>
//         </Typography>
//       </Box>
//     </Box>
//   );
// }

function MiddleSectionTopLeft() {
  return (
    <Box sx={middleSectionTopLeftContainerSx}>
      <Box component="img" src="/images/track-change-example.svg" sx={imageSx} />
    </Box>
  );
}

function MiddleSectionTopRight() {
  return (
    <Box sx={middleSectionTopRight}>
      <Box sx={sectionHeaderSx}>
        <Typography variant="display-sm-bold">{STRINGS.middleSection1.title}</Typography>
      </Box>
      <Box>
        <Typography variant="text-sm-regular">
          <ReactMarkdown>{STRINGS.middleSection1.description}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
}

function InteractiveChart() {
  return (
    <Box sx={interactiveChartContainerSx}>
      <Box component="img" src="/images/career-track-compensation-graph.svg" sx={imageSx} />

      {PROFESSIONAL_TIER_DATA.map((tier, index) => {
        // Specific positioning for each bar based on SVG layout
        const barPositions = [
          { left: '9%', width: '10%' }, // semi-skilled
          { left: '20%', width: '10%' }, // trades/technical
          { left: '32%', width: '10%' }, // middle-tier
          { left: '44%', width: '10%' }, // upper-tier
          { left: '55%', width: '10%' }, // top-tier
        ];

        const position = barPositions[index];

        return (
          <Tooltip
            key={tier.id}
            title={
              <Box sx={tooltipContentSx}>
                <Typography
                  color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-secondary-hover']}
                  variant="text-sm-bold"
                >
                  {tier.salary}
                </Typography>
                <Typography
                  color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
                  variant="text-sm-regular"
                >
                  per year
                </Typography>
              </Box>
            }
            placement="top"
            arrow
            slotProps={{
              tooltip: {
                sx: tooltipComponentSx,
              },
              arrow: {
                sx: tooltipArrowSx,
              },
            }}
          >
            <Box sx={barOverlaySx(position.left, position.width, index)} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function MiddleSectionBottomRight() {
  return (
    <Box sx={middleSectionBottomRightSx}>
      <Box
        component="img"
        src="/images/colored-career-tracks.svg"
        sx={middleSectionBottomRightImgSx}
      />
      <InteractiveChart />
    </Box>
  );
}

function MiddleSectionBottomLeft() {
  return (
    <Box sx={middleSectionBottomLeft}>
      <Box sx={sectionHeaderSx}>
        <Typography variant="display-sm-bold">{STRINGS.middleSection2.title}</Typography>
      </Box>
      <Box>
        <Typography variant="text-sm-regular">
          <ReactMarkdown>{STRINGS.middleSection2.description}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
}

// function BottomRightSection() {
//   return (
//     <Box sx={bottomRightSectionContainerSx}>
//       <Box component="img" src="/images/roles-by-track-sticker.svg" sx={topRightSectionImageSx} />
//       <Box component="img" src="/images/track-roles.svg" sx={topRightSectionImageSx} />
//     </Box>
//   );
// }

export function WhatIsACareerTrackModalContent({ onClose }: { onClose?: () => void }) {
  return (
    <Box sx={containerSx}>
      <Header onClose={onClose} />

      <Box sx={topContainerSx}>
        <TopLeftSection />
        <TopRightSection />
      </Box>

      <Box sx={middleContainerSx}>
        <MiddleSectionTopLeft />
        <MiddleSectionTopRight />
      </Box>

      <Box sx={middleSectionBottomSx}>
        <MiddleSectionBottomLeft />
        <MiddleSectionBottomRight />
      </Box>

      {/* <Box sx={fiftyfiftyGridSx}>
        <BottomLeftSection />
        <BottomRightSection />
      </Box> */}
    </Box>
  );
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100vw',
  overflowY: 'auto',
  overflowX: 'clip',
  containerType: 'inline-size',
};

const fiftyfiftyGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@container (min-width: 768px)': {
    gridTemplateColumns: '50% 50%',
  },
};

const topContainerSx: SxProps<Theme> = ({ backrs }) => ({
  ...fiftyfiftyGridSx,
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const middleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  ...fiftyfiftyGridSx,
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderTop: 'none',
});

const middleSectionBottomSx: SxProps<Theme> = (theme) => ({
  ...middleContainerSx(theme),
  borderTop: 'none',
  bgcolor: theme.backrs.colors['Brand/50'],

  // temporary
  border: 'none',
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  backgroundColor: backrs.colors['Colors/Base/Black'],
  justifyContent: 'space-between',
  padding: `${backrs.spacing['spacing-2xl']} ${backrs.spacing['spacing-3xl']}`,
  borderTopLeftRadius: backrs.radius['radius-lg'],
  borderTopRightRadius: backrs.radius['radius-lg'],
});

const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const headerSubtitleSx: SxProps<Theme> = {
  display: 'none',
  '@container (min-width: 768px)': {
    display: 'block',
  },
};

const closeIconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const sectionHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 768px)': {
    gap: backrs.spacing['spacing-lg'],
  },
});

const topLeftSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: backrs.spacing['spacing-5xl'],
  borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderLeft: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  position: 'relative',
  bgcolor: backrs.colors['Colors/Base/White'],
  '@container (min-width: 768px)': {
    borderBottom: 'none',
  },
  '@container (max-width: 400px)': {
    px: backrs.spacing['spacing-xl'],
  },
});

const topLeftSectionHeaderSx: SxProps<Theme> = (theme) => ({
  ...sectionHeaderSx(theme),
  paddingRight: theme.backrs.spacing['spacing-none'],
  '@container (min-width: 768px)': {
    paddingRight: theme.backrs.spacing['spacing-6xl'],
  },
});

const topLeftSectionImageSx: SxProps<Theme> = {
  width: '85px',
  height: '55px',
  position: 'static',
  bottom: 'auto',
  right: 'auto',
  transform: 'rotate(-23deg)',
  '@container (min-width: 768px)': {
    width: '127px',
    height: '82px',
    position: 'absolute',
    bottom: '30%',
    right: '0%',
    transform: 'translateX(50%) rotate(14deg)',
  },
};

const descriptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingRight: backrs.spacing['spacing-none'],
  '@container (min-width: 768px)': {
    paddingRight: backrs.spacing['spacing-6xl'],
  },
});

const topRightSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Brand/100'],
  borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  padding: backrs.spacing['spacing-5xl'],
  borderLeft: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  '@container (min-width: 768px)': {
    borderLeft: 'none',
  },
});

// const bottomRightSectionContainerSx: SxProps<Theme> = (theme) => ({
//   ...topRightSectionContainerSx(theme),
//   flexDirection: 'column',
//   padding: theme.backrs.spacing['spacing-5xl'],
//   justifyContent: 'flex-start',
//   gap: theme.backrs.spacing['spacing-3xl'],
//   borderBottomRightRadius: theme.backrs.radius['radius-lg'],
//   borderBottomLeftRadius: {
//     xs: theme.backrs.radius['radius-lg'],
//     md: theme.backrs.radius['radius-none'],
//   },
//   borderBottom: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
//   '@media (max-width: 400px)': {
//     px: theme.backrs.spacing['spacing-xl'],
//   },
// });

const imageContainerSectionSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-5xl'],
  px: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    px: backrs.spacing['spacing-5xl'],
  },
});

const middleSectionTopLeftContainerSx: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.backrs.colors['Colors/Base/White'],
  ...imageContainerSectionSx(theme),
  order: 1,
  paddingTop: theme.backrs.spacing['spacing-none'],
  '@container (min-width: 768px)': {
    order: 0,
    paddingTop: theme.backrs.spacing['spacing-5xl'],
  },
});

const middleSectionBottomRightSx: SxProps<Theme> = (theme) => ({
  ...imageContainerSectionSx(theme),
  bgcolor: 'transparent',
  gap: 1,
  paddingTop: theme.backrs.spacing['spacing-none'],
  flexDirection: 'column',

  // temporary until bottom section is returned
  border: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
  borderTop: 'none',
  borderLeft: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
  borderRight: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
  borderBottomRightRadius: theme.backrs.radius['radius-lg'],
  borderBottomLeftRadius: theme.backrs.radius['radius-lg'],
  '@container (min-width: 768px)': {
    paddingTop: theme.backrs.spacing['spacing-5xl'],
    flexDirection: 'row',
    borderLeft: 'none',
    borderBottomLeftRadius: theme.backrs.radius['radius-none'],
  },
});

const imageSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
};

const middleSectionBottomRightImgSx: SxProps<Theme> = {
  ...imageSx,
  order: 1,
  '@container (min-width: 768px)': {
    order: 0,
  },
};

const topRightSectionImageSx: SxProps<Theme> = ({ backrs }) => ({
  ...imageSx,
  backgroundColor: backrs.colors['Brand/100'],
});

// const bottomLeftSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
//   display: 'flex',
//   flexDirection: 'column',
//   backgroundColor: backrs.colors['Colors/Base/White'],
//   padding: backrs.spacing['spacing-5xl'],
//   gap: backrs.spacing['spacing-md'],
//   border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
//   borderTop: 'none',
//   borderBottomLeftRadius: {
//     xs: backrs.radius['radius-none'],
//     md: backrs.radius['radius-lg'],
//   },
//   '@media (max-width: 400px)': {
//     px: backrs.spacing['spacing-xl'],
//   },
// });

const middleSection: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  flex: 1,
  padding: backrs.spacing['spacing-5xl'],
  '@container (max-width: 400px)': {
    px: backrs.spacing['spacing-xl'],
  },
});

const middleSectionTopRight: SxProps<Theme> = (theme: Theme) => ({
  ...middleSection(theme),
  backgroundColor: theme.backrs.colors['Colors/Base/White'],
});

const middleSectionBottomLeft: SxProps<Theme> = (theme) => ({
  ...middleSection(theme),
  backgroundColor: theme.backrs.colors['Brand/50'],
  paddingBottom: theme.backrs.spacing['spacing-none'],

  // temporary until bottom section is returned
  border: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
  borderTop: 'none',
  borderRight: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
  borderBottom: 'none',
  borderBottomLeftRadius: theme.backrs.radius['radius-none'],
  '@container (min-width: 768px)': {
    paddingBottom: theme.backrs.spacing['spacing-5xl'],
    borderRight: 'none',
    borderBottom: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
    borderBottomLeftRadius: theme.backrs.radius['radius-lg'],
  },
});

const interactiveChartContainerSx: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
  maxHeight: '100%',
};

const barOverlaySx = (left: string, width: string, index: number): SxProps<Theme> => ({
  position: 'absolute',
  cursor: 'pointer',
  borderRadius: '4px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
  left: left,
  width: width,
  height: `${15 + index * 12}%`, // Height increases with tier
  bottom: '25%',
});

const tooltipContentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const tooltipComponentSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  maxWidth: '200px',
  boxShadow: backrs.shadows['shadow-md'],
  border: '1px solid black',
});

const tooltipArrowSx: SxProps<Theme> = ({ backrs }) => ({
  color: 'white',
  '&::before': {
    boxShadow: backrs.shadows['shadow-md'],
  },
});
